import {Injectable} from '@angular/core';
import {RestApiService} from '../../../../shared/services/rest-api.service';
import {SessionService} from '../session/session.service';
import {runtimeConfig} from '../../../../shared/utils/config-utils';
import {CoverageGroup, OcConfiguration} from '../../../configuration/oc-configuration';
import {LanguageService} from '../../../../shared/services/language.service';
import {RequestMetricResult} from '../../../../shared/components/models/metrics/request-metric-result.enum';
import {MetricsService} from '../../../../shared/services/metrics.service';
import {ApplicationStateService} from '../../../../shared/services/application-state.service';
import {salesChannel} from '../../../utils/utils';
import {UiConfig} from '../../../configuration/ui-config';
import {InternalConfig} from './internal-config';
import {AgreementModel} from '../../../models/agreement.model';
import {UrlParamsService} from '../url-params.service';
import {PartnerService} from '../../../../shared/services/partner.service';
import {ResourceService} from '../../../../shared/services/resource.service';
import {RESOURCES_CONFIG} from '../../../configs/resources-config';
import {ApplicationContextService} from './application-context.service';
import {StepKey} from '../../../enums/step-key.enum';
import {StepConfig} from '../../../configuration/step-config';
import {SalesChannel} from '../../../consts/sales-channel.enum';
import {TestingPresetsService} from '../../../../shared/services/debugging/testing-presets.service';
import {TESTING_PRESETS_CONFIG} from '../../../configs/testing-presets-config';
import {QueryParamName} from '../../../enums/query-param-name.enum';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {

  initializingConfiguration: Promise<void>;
  configuration: OcConfiguration;

  get coverageGroups(): CoverageGroup[] {
    return this.configuration.coverageGroups;
  }

  get uiConfig(): UiConfig {
    return this.configuration.uiConfig;
  }

  get partnerAgreements(): AgreementModel[] {
    return this.configuration.partnerAgreements;
  }

  private readonly CLASSNAME = 'ConfigurationService';
  private readonly SEO_CONTENT_VISIBLE_STEPS: StepKey[] = [
    StepKey.FIRST_STEP_0,
    StepKey.FIRST_STEP_1,
    StepKey.FIRST_STEP_2,
    StepKey.FIRST_STEP_3,
  ];

  private readonly SEO_CONTENT_VISIBLE_SALES_CHANNELS: string[] = [
      SalesChannel.KIOSKPOLIS,
  ];

  constructor(private api: RestApiService,
              private language: LanguageService,
              private sessionService: SessionService,
              private metricsService: MetricsService,
              private urlParamsService: UrlParamsService,
              private partnerService: PartnerService,
              private resourceService: ResourceService,
              private contextService: ApplicationContextService,
              private applicationStateService: ApplicationStateService) {
    this.initializingConfiguration = this.createConfiguration();
  }

  createConfiguration() {
    let mockConfig = false;

    let promise;
    if (mockConfig) {
      promise = this.api.get('assets/jsons/configuration.json')
    } else {
      let uri = this.api.baseUri + this.api.prepareUri('oc-ac', 'configurations', '1.0.0', 'ui');
      promise = this.api.get(uri, {
        configurationCode: runtimeConfig().configurationCode,
      });
    }

    let metricName = {
      className: this.CLASSNAME,
      methodName: 'createConfiguration'
    };

    return promise
      .then(resp => {
        this.configuration = resp;

        this.assignInternalConfiguration();
        this.language.setDefaultLanguage(this.uiConfig.defaultLanguage);
        this.applicationStateService.salesChannel = salesChannel();
        this.urlParamsService.init(this.configuration.uiConfig.dictionaries);
        this.partnerService.init(this.configuration.uiConfig.partnerNameExpirationDays, this.configuration.uiConfig.portalPartnerDataExpirationDays);
        this.metricsService.initIPAddress(this.configuration.uiConfig.retrieveIPFromExternalService);
        this.resourceService.init(this.configuration.uiConfig.resourcesEndpoint, RESOURCES_CONFIG);
        TestingPresetsService.init(TESTING_PRESETS_CONFIG, this.urlParamsService.getQueryParam(QueryParamName.TESTING_PRESETS));
        return this.sessionService.getSession(this.uiConfig.sessionEndpoint);
      })
      .catch(error => {
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.ERROR
        }, error);
        console.error(error);
        return Promise.reject(error);
      });
  }

  shouldDisplaySeoContent(stepConfig: StepConfig): boolean {
    if (this.contextService.isDiagnosticStationContext) {
      return false;
    }
    if (this.SEO_CONTENT_VISIBLE_SALES_CHANNELS.includes(salesChannel())) {
      return stepConfig ? this.SEO_CONTENT_VISIBLE_STEPS.includes(stepConfig.stepKey) : false;
    }
    return false;
  }

  initApplicationContext(): void {
    this.contextService.init();
    this.updateStepsConfigWithContext();
  }

  private assignInternalConfiguration(): void {
    Object.assign(this.configuration.uiConfig, InternalConfig.config[salesChannel()]);
  }

  private updateStepsConfigWithContext(): void {
    if (this.contextService.stepsConfigDefined) {
      this.configuration.uiConfig.navigateConfig.steps.forEach(step => {
        if (this.contextService.isStepDisabled(step.stepKey)) {
          step.sections = [];
        } else {
          step.sections.forEach(section => {
            if (this.contextService.isSectionDisabled(step.stepKey, section.key)) {
              step.sections = step.sections.filter(sectionConfig => sectionConfig.key !== section.key);
            }
          });
          if (!!step.sectionsUnderStepButton) {
            step.sectionsUnderStepButton.forEach(section => {
              if (this.contextService.isSectionDisabled(step.stepKey, section.key)) {
                step.sectionsUnderStepButton = step.sectionsUnderStepButton.filter(sectionConfig => sectionConfig.key !== section.key);
              }
            });
          }
        }
      });
    }
  }
}
